import React, {
  Reducer,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import { FindImagesModalView, FindImagesModalViewProps } from './FindImagesModal.view';
import {
  FindImagesFilterAction,
  FindImagesFilterType,
  MediaItem,
  MediaItemSelectedType
} from '../../../../models/media';
import { AppContext } from '../../../../providers/AppProvider';
import { MediaLibraryHelpers } from '../mediaLibraryHelpers';
import { Void } from '../../../../models/basic-objects';
import {
  ImageType,
  MediaType,
  Orientation,
  SearchGiphyLazyQueryHookResult,
  SearchPixabayLazyQueryHookResult,
  SearchUnsplashLazyQueryHookResult
} from '../../../../models/graphql';
import { FindImagesSource } from '../../../../constants/media';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { useForm } from 'react-hook-form';
import { FormOptions } from '../../../../constants/form';
import { MediaGridContext } from '../MediaGrid/MediaGrid.view';
import { FileWithPreview } from '../AddMediaSelectorModal/AddMediaSelectorModal.view';
import { ExternalMedia, SourceType } from '../../../../hooks/useMediaUpload';
import { useApolloClient } from '@apollo/client';

export type FindImagesModalControllerProps = {
  context: MediaGridContext;
  onClose: Void;
  loadingInitial: boolean;
  loadingMore: boolean;
  fileUploadHandler: (files: FileWithPreview[] | ExternalMedia[]) => void;
  initialPixabayImagesSearch: (filters: { orientation: Orientation; type: ImageType }, term: string) => void;
  initialGiphyImagesSearch: (term: string) => void;
  initialUnsplashImagesSearch: (term: string) => void;
  fetchMorePixabayImages: Void;
  fetchMoreGiphyImages: Void;
  fetchMoreUnsplashImages: Void;
  searchPixabayResults: SearchPixabayLazyQueryHookResult[1];
  searchUnsplashResults: SearchUnsplashLazyQueryHookResult[1];
  searchGiphyResults: SearchGiphyLazyQueryHookResult[1];
  shareItemAction: FindImagesModalViewProps['shareItemAction'];
  imagesIdsWithErrorSelected: MediaItemSelectedType[];
  setImagesIdsWithErrorSelected: React.Dispatch<React.SetStateAction<MediaItemSelectedType[]>>;
};

export const FindImagesModalController = (props: FindImagesModalControllerProps): JSX.Element => {
  const { cache } = useApolloClient();
  const { selectedWorkspaceId } = useContext(AppContext);
  const [selectedMediaItems, setSelectedMediaItems] = useState<MediaItemSelectedType[]>([]);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filters, dispatchFilters] = useReducer<Reducer<FindImagesFilterType, FindImagesFilterAction>>(
    MediaLibraryHelpers.findImagesFilterReducer,
    {
      type: '',
      source: FindImagesSource.PIXABAY,
      proportion: '',
      tempType: '',
      tempSource: '',
      tempProportion: '',
      resetAll: ''
    }
  );

  const { source, type, proportion } = filters;

  const rightColRef = useRef<HTMLDivElement>(null);
  const formInstance = useForm({
    ...FormOptions,
    mode: 'onSubmit',
    defaultValues: {
      term: ''
    }
  });

  const onSubmit: SubmitHandler<{ term: string }> = (data: { term: string }, ev) => {
    ev?.preventDefault();
    setSearchTerm(data.term);
  };

  const fileUploadHandler = (media: MediaItem[]) => {
    const urls: ExternalMedia[] = [];
    const ids = media.map(item => item.id);

    ids.forEach(id => {
      const item = mediaItems.find(item => item?.id === id);

      if (item?.url) {
        urls.push({ sourceType: SourceType.external, url: item.url, type: MediaType.PHOTO });
      }
    });

    // Remove any uploaded items from the selected list
    let selectedMediaItemsClone: MediaItemSelectedType[] = [];
    selectedMediaItems.forEach(item => {
      if (!ids.includes(item.id)) {
        selectedMediaItemsClone.push({ id: item.id, type: item.type });
      }
    });

    mediaItems.forEach((item, index) => {
      if (ids.includes(item.id)) {
        delete mediaItems[index];
      }
    });

    setSelectedMediaItems(() => selectedMediaItemsClone);

    props.fileUploadHandler(urls);
  };
  const { initialPixabayImagesSearch, initialUnsplashImagesSearch, initialGiphyImagesSearch } = props;
  const startFindImagesSearch = useCallback(() => {
    switch (filters.source) {
      case FindImagesSource.PIXABAY:
        const imageType: ImageType = Object.values(ImageType).find(typeFilter => typeFilter === type) || ImageType.ALL;
        const orientation: Orientation = Object.values(Orientation).find(o => o === proportion) || Orientation.ALL;
        initialPixabayImagesSearch({ orientation: orientation, type: imageType }, searchTerm);
        break;
      case FindImagesSource.UNSPLASH:
        initialUnsplashImagesSearch(searchTerm);
        break;

      case FindImagesSource.GIPHY:
        initialGiphyImagesSearch(searchTerm);
        break;
    }
  }, [
    filters.source,
    initialGiphyImagesSearch,
    initialPixabayImagesSearch,
    initialUnsplashImagesSearch,
    proportion,
    searchTerm,
    type
  ]);

  const searchPixabay = props.searchPixabayResults.data?.searchPixabay;
  const searchUnsplash = props.searchUnsplashResults.data?.searchUnsplash;
  const searchGiphy = props.searchGiphyResults.data?.searchGiphy;

  const { loadingInitial, loadingMore, fetchMorePixabayImages, fetchMoreUnsplashImages, fetchMoreGiphyImages } = props;
  const fetchMoreImagesSearch = useCallback(() => {
    // Don't try to fetch more if we are loading
    if (loadingInitial || loadingMore) return;
    switch (source) {
      case FindImagesSource.PIXABAY:
        fetchMorePixabayImages();
        break;
      case FindImagesSource.UNSPLASH:
        fetchMoreUnsplashImages();
        break;
      case FindImagesSource.GIPHY:
        fetchMoreGiphyImages();
        break;
    }
  }, [loadingInitial, loadingMore, source, fetchMorePixabayImages, fetchMoreUnsplashImages, fetchMoreGiphyImages]);

  const mediaItems: MediaItem[] = useMemo(() => {
    switch (source) {
      case FindImagesSource.PIXABAY:
        return MediaLibraryHelpers.mapThirdPartyItemToMediaItems(searchPixabay);
      case FindImagesSource.UNSPLASH:
        return MediaLibraryHelpers.mapThirdPartyItemToMediaItems(searchUnsplash);
      case FindImagesSource.GIPHY:
        return MediaLibraryHelpers.mapThirdPartyItemToMediaItems(searchGiphy);
      default:
        return [];
    }
  }, [source, searchPixabay, searchUnsplash, searchGiphy]);

  const scrollToTopOfElement = (ref: RefObject<HTMLDivElement>) => {
    if (ref.current && ref.current.parentElement) {
      ref.current.parentElement.scrollTop = 0;
    }
  };

  const evictAllSearchCache = useCallback(() => {
    cache.modify({
      id: cache.identify({ __typename: 'Query' }),
      fields: {
        searchPixabay() {
          return [];
        },
        searchUnsplash() {
          return [];
        },
        searchGiphy() {
          return [];
        }
      }
    });

    cache.gc();
  }, [cache]);

  useEffect(() => {
    return () => {
      evictAllSearchCache();
    };
  }, [evictAllSearchCache]);

  useEffect(() => {
    scrollToTopOfElement(rightColRef);
    evictAllSearchCache();
    setSearchTerm(() => formInstance.getValues('term'));
    startFindImagesSearch();
  }, [
    selectedWorkspaceId,
    source,
    type,
    proportion,
    formInstance,
    searchTerm,
    evictAllSearchCache,
    startFindImagesSearch
  ]);

  const viewProps: FindImagesModalViewProps = {
    ...props,
    loadingInitial: props.loadingInitial,
    loadingMore: props.loadingMore,
    mediaItems,
    formInstance,
    filters,
    dispatchFilters,
    showFilterModal,
    onSubmit,
    setShowFilterModal,
    setSearchTerm,
    selectedMediaItems,
    setSelectedMediaItems,
    fileUploadHandler,
    fetchMoreMediaItemsHandler: fetchMoreImagesSearch
  };
  return <FindImagesModalView {...viewProps} ref={rightColRef} />;
};
